import { adminApiConfig, getStoredAdminSession } from './admin-auth';

export type NotificationEvent = 'booking_received'|'time_assigned'|'approved'|'payment_requested'|'confirmed'|'rescheduled'|'declined'|'cancelled';
export type NotificationChannel = 'email'|'whatsapp';
export type NotificationStatus = 'queued'|'processing'|'sent'|'failed'|'configuration_required'|'skipped'|'manual_ready'|'manual_sent'|'superseded';

export type CustomerNotification = {
  id:string;
  booking_id:string;
  customer_id:string;
  event_type:NotificationEvent;
  channel:NotificationChannel;
  recipient:string|null;
  subject:string;
  message_text:string;
  status:NotificationStatus;
  provider:string|null;
  provider_message_id:string|null;
  attempt_count:number;
  last_error:string|null;
  created_at:string;
  updated_at:string;
  sent_at:string|null;
  bookings?:{reference:string;service_name:string;appointment_date:string;appointment_time:string|null}|null;
  customers?:{full_name:string;whatsapp:string;whatsapp_normalized?:string|null;email:string|null}|null;
};

export type NotificationDispatchResult = {
  provider:string;
  provider_configured:boolean;
  email_enabled:boolean;
  sender_configured?:boolean;
  pending_count?:number;
  sent?:number;
  failed?:number;
  configuration_required?:number;
  superseded?:number;
  message?:string;
};

function authHeaders(extra?:Record<string,string>){
  const session=getStoredAdminSession();
  if(!session)throw new Error('Your admin session has expired. Sign in again.');
  return{
    apikey:adminApiConfig.publishableKey,
    Authorization:`Bearer ${session.access_token}`,
    'Content-Type':'application/json',
    ...extra,
  };
}

async function parseError(response:Response,fallback:string){
  const body=await response.json().catch(()=>({})) as {message?:string;error_description?:string};
  return body.message||body.error_description||fallback;
}

export async function getAdminNotifications(limit=200):Promise<CustomerNotification[]>{
  const select='*,bookings(reference,service_name,appointment_date,appointment_time),customers(full_name,whatsapp,whatsapp_normalized,email)';
  const response=await fetch(`${adminApiConfig.url}/rest/v1/customer_notifications?select=${encodeURIComponent(select)}&order=created_at.desc&limit=${Math.min(Math.max(limit,1),500)}`,{headers:authHeaders({Accept:'application/json'})});
  if(!response.ok)throw new Error(await parseError(response,'Unable to load customer notifications.'));
  return response.json() as Promise<CustomerNotification[]>;
}

export async function dispatchBookingNotifications(input:{bookingId?:string;notificationIds?:string[];limit?:number;dryRun?:boolean}={}):Promise<NotificationDispatchResult>{
  const response=await fetch(`${adminApiConfig.url}/functions/v1/dispatch-booking-notifications`,{
    method:'POST',
    headers:authHeaders(),
    body:JSON.stringify({
      booking_id:input.bookingId,
      notification_ids:input.notificationIds,
      limit:input.limit??20,
      dry_run:input.dryRun??false,
    }),
  });
  if(!response.ok)throw new Error(await parseError(response,'Unable to process customer notifications.'));
  return response.json() as Promise<NotificationDispatchResult>;
}

async function callNotificationRpc(name:string,id:string):Promise<CustomerNotification>{
  const response=await fetch(`${adminApiConfig.url}/rest/v1/rpc/${name}`,{
    method:'POST',
    headers:authHeaders(),
    body:JSON.stringify({p_notification_id:id}),
  });
  if(!response.ok)throw new Error(await parseError(response,'Unable to update the notification.'));
  const result=await response.json() as CustomerNotification|CustomerNotification[];
  const row=Array.isArray(result)?result[0]:result;
  if(!row)throw new Error('Supabase did not return the updated notification.');
  return row;
}

export function requeueNotification(id:string){return callNotificationRpc('requeue_customer_notification',id)}
export function markWhatsAppNotificationSent(id:string){return callNotificationRpc('mark_whatsapp_notification_sent',id)}
